import { InternalUrls } from '/@/modules/core/app/constants/app.constants';
import { HttpMethod } from '/@/modules/core/app/enums/HttpMethod';
import { MimeType } from '/@/modules/core/app/enums/MimeType';
import { BaseUrlBuilder } from '/@/modules/core/app/helpers/BaseUrlBuilder';
import Logger from '/@/modules/core/app/helpers/Logger';
import { commits } from '/@/modules/core/app/helpers/store';

class DocumentUIService {
  /**
   * @param {string} lang
   */
  setLanguage(lang) {
    document.querySelector('html').setAttribute('lang', lang);
  }

  /**
   * @param {string} theme
   */
  setTheme(theme) {
    document.querySelector('html').setAttribute('data-theme', theme);
  }

  clearTheme() {
    document.querySelector('html').removeAttribute('data-theme');
  }

  reloadApp() {
    window.location.reload();
  }

  reloadAppAtRoot() {
    window.location.href = window.location.origin;
  }

  reloadAppAtPath(path) {
    window.location.href = `${window.location.origin}/${path}`;
  }

  /**
   * @param {string} location
   */
  setLocation(location) {
    window.location.href = location;
  }

  setTopLocation(location) {
    window.top.location.href = location;
  }

  getUrl() {
    return window.location.href;
  }

  setTitle(title) {
    document.title = title;
  }

  isTabActive() {
    return !document.hidden;
  }

  /**
   * @param {string} metaName
   * @param {string} content
   */
  setMeta(metaName, content) {
    document.querySelector('meta').setAttribute('name', metaName);
    document.querySelector(`meta[name="${metaName}"]`).setAttribute('content', content);
  }

  openUrl(url, windowName = '_blank', options = '') {
    const myWindow = window.open(url, windowName, options);
    myWindow.focus();
  }

  openAppUrl(path, windowName = '_blank', options = '') {
    return this.openUrl(`${window.location.origin}/${path}`, windowName, options);
  }

  navigateToClearEstateWebsite() {
    window.location = InternalUrls.CLEAR_ESTATE_WEBSITE;
  }

  /**
   * @param {axios.AxiosResponse} response
   * @param {?string} filename
   * @returns {void}
   */
  downloadFile(response, filename = null) {
    if (!filename) {
      const parts = response.config.url.split('/');
      filename = parts[parts.length - 1];
    }

    this.downloadFileFromBlob(new Blob([response.data]), filename);
  }

  /**
   * @param {Blob} blob
   * @param {?string} filename
   */
  downloadFileFromBlob(blob, filename = null) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
  }

  /**
   * @returns {HTMLScriptElement}
   */
  addDynamicScript(id, src, options = {}) {
    const existingScript = document.getElementById(id);

    if (!existingScript) {
      const script = document.createElement('script');
      script.src = src;
      script.id = id;

      if (options.type) {
        script.type = options.type;
      }
      if (options.attributes) {
        Object.keys(options.attributes).forEach((key) => {
          script.setAttribute(key, options.attributes[key]);
        });
      }

      script.onload = options.onload;
      document.body.appendChild(script);

      return script;
    } else {
      options.onload?.();
    }

    return existingScript;
  }

  addDynamicScriptAsync(id, src, options = {}) {
    return new Promise((resolve, reject) => {
      try {
        options.onload = () => {
          resolve();
        };
        this.addDynamicScript(id, src, options);
      } catch (e) {
        Logger.error(e.message);
        reject(e);
      }
    });
  }

  addDynamicStyle(id, src, options = {}) {
    const existingLink = document.getElementById(id);

    if (!existingLink) {
      const link = document.createElement('link');
      link.href = src;
      link.id = id;
      link.onload = options.onload;
      document.body.appendChild(link);
    } else {
      options.onload?.();
    }
  }

  removeElement(id) {
    const element = document.getElementById(id);
    if (element) {
      element.parentNode.removeChild(element);
    }
  }

  removeAppLoader() {
    document.getElementById('app-loader')?.remove();
  }

  listenToPopState() {
    window.addEventListener('popstate', () => {
      commits.ui.setPopStateDetected(true);
    });
  }

  toggleBeforeUnloadConfirmation(shouldConfirm) {
    window.onbeforeunload = shouldConfirm ? () => true : null;
  }

  getViewportHeight() {
    return window.innerHeight;
  }

  getViewportWidth() {
    return window.innerWidth;
  }

  addEventListener(event, handler) {
    window.addEventListener(event, handler);
  }

  removeEventListener(event, handler) {
    window.removeEventListener(event, handler);
  }

  dispatchEvent(event) {
    window.dispatchEvent(event);
  }

  sendBeacon(url, data, method = HttpMethod.PUT) {
    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
      keepalive: true
    });
  }

  postMessageToIframe(iframeId, message, targetOrigin) {
    document.getElementById(iframeId).contentWindow.postMessage(message, targetOrigin);
  }

  /**
   * @param {axios.AxiosResponse} response
   * @returns {void}
   */
  openFileInNewTab(response) {
    const blob = new Blob([response.data], { type: MimeType.APPLICATION_PDF });
    const url = window.URL.createObjectURL(blob);
    this.openUrl(url);
  }

  getTableRowHighlightedAnimationDurationInMilliseconds() {
    return (
      window
        .getComputedStyle(document.body)
        .getPropertyValue('--ce-table-row-highlighted-animation-duration')
        .replace('s', '') * 1000
    );
  }

  constructUrl(path, queryParams = {}, shouldEncode = true) {
    const root = `${window.location.origin}/${path}`;
    const builder = new BaseUrlBuilder(root);

    Object.entries(queryParams).forEach(([key, value]) => {
      builder.setParam(key, value, false, shouldEncode);
    });

    return builder.build();
  }
}

export default new DocumentUIService();
